import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useHelpStore } from '@/stores/helpStore';
import { pageContext } from '@/lib/pageContext';
import { useAuthStore } from '@/stores/authStore';
import { useConsultAssistant } from '@/stores/consultAssistantStore';
import { useAssistantContext } from '@/stores/assistantContextStore';
import { useAssistantUi, type AssistantTab } from '@/stores/assistantUiStore';
import { useUnreadTotal } from '@/stores/chatStore';
import NursePanel from '@/components/NurseAssistant';
import MessagesPanel from '@/components/MessagesPanel';
import { useVoiceInput } from '@/hooks/useVoiceInput';
import { askAssistant, type AssistantAction } from '@/lib/api/assistant';
import { resolveBooking, type BookingResolution, type BookingSlot, type ActionAppointment } from '@/lib/api/assistantBooking';
import { resolveClinicalCommand } from '@/lib/api/clinicalCommand';
import { bookAppointment, cancelAppointment, rescheduleAppointment } from '@/lib/api/appointments';
import { openRecommendation, openTreatmentOffer, applyRecommendationDraft } from '@/lib/api/consultations';

type BookingUi =
  | { kind: 'ambiguous'; command: string; candidates: { patientId: string; name: string }[] }
  | { kind: 'propose'; res: BookingResolution }
  | { kind: 'cancel'; res: BookingResolution };

interface ChatMsg { role: 'user' | 'assistant'; text: string; action?: AssistantAction | null; booking?: BookingUi | null; booked?: boolean; guide?: string | null }

/** One surface for both halves of "talking": the AI, staff messages, and (in a visit) the nurse. */
// Kept in step with the store's AssistantTab rather than restated — the two had already
// drifted apart once, which is how a tab can exist in the store and be unrenderable here.
type Tab = AssistantTab;

const fmtSlot = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });


const AiAssistant: React.FC = () => {
  const { t } = useTranslation('common');
  const openHelp = useHelpStore((st) => st.openHelp);
  const location = useLocation();
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.role);
  const aiEnabled = useAuthStore((s) => s.aiEnabled);
  const consult = useConsultAssistant();
  const bookingCabinetId = useAssistantContext((s) => s.cabinetId);
  const unread = useUnreadTotal();

  // What this clinic still needs to sort out. Loaded once for the session; the panel filters by
  // route rather than asking again, so walking around the app costs nothing.

  // Open/tab live in a store so a deep link can open the panel on the right tab — see
  // assistantUiStore. Everything else about the assistant stays local.
  const open = useAssistantUi((s) => s.open);
  const setOpen = useAssistantUi((s) => s.setOpen);
  const tab = useAssistantUi((s) => s.tab);
  const setTab = useAssistantUi((s) => s.setTab);
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [busyAction, setBusyAction] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Example commands drop the phrasing into the box (not sent) so the user swaps in a real patient name.
  const fillExample = (text: string) => {
    setInput(text);
    inputRef.current?.focus();
  };

  useEffect(() => {
    if (open) scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, loading, open]);

  // The open record (if any): the backend uses its id to add pseudonymised clinical facts.
  const consultationId = location.pathname.match(/^\/consultations\/([0-9a-fA-F-]{36})/)?.[1];
  const patientId = location.pathname.match(/^\/patients\/([0-9a-fA-F-]{36})/)?.[1];
  const hasRecord = !!(consultationId || patientId);

  const say = (text: string, extra?: Partial<ChatMsg>) =>
    setMessages((m) => [...m, { role: 'assistant', text, ...extra }]);

  // Render an appointment resolution as assistant messages (disambiguation / proposal / cancel / not-found).
  const showBooking = (res: BookingResolution, command: string) => {
    switch (res.status) {
      case 'no_patient':
        say(t('assistant.bkNoPatient', "I couldn't find that patient. Try their full name."));
        break;
      case 'ambiguous':
        say(t('assistant.bkWhich', 'Which patient did you mean?'), { booking: { kind: 'ambiguous', command, candidates: res.candidates } });
        break;
      case 'no_cabinet':
        say(t('assistant.bkNoCabinet', 'No bookable location is set up yet.'));
        break;
      case 'no_appointment':
        say(t('assistant.bkNoAppt', '{{name}} has no upcoming appointment.', { name: res.patientName ?? '' }));
        break;
      case 'no_slots':
        say(t('assistant.bkNoSlots', 'No free slots {{when}}. Try another time.', { when: res.whenLabel ?? '' }));
        break;
      case 'confirm_cancel':
        say(t('assistant.bkCancelIntro', 'Cancel which of {{name}}’s appointments?', { name: res.patientName ?? '' }),
          { booking: { kind: 'cancel', res } });
        break;
      case 'propose':
        say(res.intent === 'reschedule'
          ? t('assistant.bkRescheduleIntro', 'Move {{name}}’s appointment — pick a new time ({{when}}):', { name: res.patientName ?? '', when: res.whenLabel ?? '' })
          : t('assistant.bkProposeIntro', 'Booking {{name}} — pick a time ({{when}}):', { name: res.patientName ?? '', when: res.whenLabel ?? '' }),
          { booking: { kind: 'propose', res } });
        break;
    }
  };

  const send = async (text: string) => {
    const q = text.trim();
    if (!q || loading) return;
    setInput('');
    setError(null);
    const history = messages.map((m) => ({ role: m.role, text: m.text }));
    setMessages((m) => [...m, { role: 'user', text: q }]);
    setLoading(true);
    try {
      // Booking-first: the appointment resolver handles scheduling commands; everything else → the assistant.
      const booking = await resolveBooking(q, bookingCabinetId ?? undefined, patientId);
      if (booking.status !== 'not_booking') { showBooking(booking, q); return; }

      // Clinical commands: on an editable open consultation, turn "mark tooth 26 as caries" into
      // confirm-first chart/notes proposals (reuses the nurse-dictation apply pipeline on the page).
      if (consult.consultationId && !consult.readOnly && consult.onDictation) {
        const clin = await resolveClinicalCommand(consult.consultationId, q);
        if (clin.status === 'extracted') {
          consult.onDictation({ sessionId: '', transcript: q, extractedFieldsJson: clin.fieldsJson, tokensUsed: 0 });
          say(t('assistant.clinicApplied', 'Done — I added the proposed changes to the consultation. Review and apply them on the chart / notes.'));
          return;
        }
        if (clin.status === 'readonly') { say(t('assistant.clinicReadonly', 'This consultation is finalised and can no longer be edited.')); return; }
        // empty / not_clinical / not_found → fall through to the normal assistant.
      }

      const reply = await askAssistant(q, pageContext(location.pathname, location.search, t), role, { patientId, consultationId }, history);
      setMessages((m) => [...m, { role: 'assistant', text: reply.answer, action: reply.action, guide: reply.guide ?? null }]);
    } catch {
      setError(t('assistant.error', 'Could not get an answer. Check that AI is configured and the wallet balance.'));
    } finally {
      setLoading(false);
    }
  };

  // Execute a proposed action only after the user clicks confirm. Everything is confirm-first.
  const runAction = async (index: number, action: AssistantAction) => {
    setBusyAction(index); setError(null);
    try {
      if (action.type === 'navigate' && action.to) {
        navigate(action.to);
        setOpen(false);
      } else if (action.type === 'open_recommendation' && consultationId) {
        await openRecommendation(consultationId);
      } else if (action.type === 'open_treatment_offer' && consultationId) {
        await openTreatmentOffer(consultationId);
      } else if (action.type === 'draft_recommendation' && consultationId) {
        await applyRecommendationDraft(consultationId);
        await openRecommendation(consultationId);
        setMessages((m) => [...m, { role: 'assistant', text: t('assistant.doneDraft', 'Done — I drafted the recommendation letter and opened it. You can review and edit it on the consultation.') }]);
      }
      // Consume the action so its button can't be clicked twice.
      setMessages((m) => m.map((msg, i) => (i === index ? { ...msg, action: null } : msg)));
    } catch {
      setError(t('assistant.actionError', 'Could not complete that action.'));
    } finally {
      setBusyAction(null);
    }
  };

  // Disambiguation: the user picked which patient — re-resolve with their id to get slots.
  const pickCandidate = async (command: string, pid: string) => {
    setLoading(true); setError(null);
    try { showBooking(await resolveBooking(command, undefined, pid), command); }
    catch { setError(t('assistant.error', 'Could not get an answer. Check that AI is configured and the wallet balance.')); }
    finally { setLoading(false); }
  };

  // Confirm-first: apply the chosen slot — a reschedule of an existing appointment, or a new booking.
  const bookSlot = async (res: BookingResolution, slot: BookingSlot, index: number) => {
    if (!res.patientId || !res.cabinetId) return;
    setBusyAction(index); setError(null);
    try {
      if (res.intent === 'reschedule' && res.rescheduleAppointmentId) {
        await rescheduleAppointment(res.rescheduleAppointmentId, slot.startUtc, slot.endUtc, slot.seatId);
        setMessages((m) => m.map((msg, i) => (i === index ? { ...msg, booking: null, booked: true } : msg)));
        say(t('assistant.bkRescheduled', 'Moved {{name}} to {{when}} with {{doctor}}.',
          { name: res.patientName ?? '', when: fmtSlot(slot.startUtc), doctor: slot.doctorName }));
      } else {
        await bookAppointment({
          cabinetId: res.cabinetId, seatId: slot.seatId, doctorId: slot.doctorId, patientId: res.patientId,
          startAt: slot.startUtc, endAt: slot.endUtc, type: 'InPerson',
        });
        setMessages((m) => m.map((msg, i) => (i === index ? { ...msg, booking: null, booked: true } : msg)));
        say(t('assistant.bkBooked', 'Booked {{name}} — {{when}} with {{doctor}}.',
          { name: res.patientName ?? '', when: fmtSlot(slot.startUtc), doctor: slot.doctorName }));
      }
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      setError(status === 409
        ? t('assistant.bkTaken', 'That slot was just taken — pick another.')
        : t('assistant.actionError', 'Could not complete that action.'));
    } finally { setBusyAction(null); }
  };

  // Confirm-first: cancel the chosen appointment.
  const cancelAppt = async (res: BookingResolution, appt: ActionAppointment, index: number) => {
    setBusyAction(index); setError(null);
    try {
      await cancelAppointment(appt.appointmentId);
      setMessages((m) => m.map((msg, i) => (i === index ? { ...msg, booking: null, booked: true } : msg)));
      say(t('assistant.bkCancelled', 'Cancelled {{name}}’s appointment on {{when}}.',
        { name: res.patientName ?? '', when: fmtSlot(appt.startUtc) }));
    } catch {
      setError(t('assistant.actionError', 'Could not complete that action.'));
    } finally { setBusyAction(null); }
  };

  const renderBooking = (bk: BookingUi, i: number) => {
    if (bk.kind === 'ambiguous') {
      return (
        <div className="mt-1.5 flex flex-col items-start gap-1">
          {bk.candidates.map((c) => (
            <button
              key={c.patientId}
              onClick={() => pickCandidate(bk.command, c.patientId)}
              className="rounded-lg border border-brand-300 bg-brand-50 px-3 py-1.5 text-xs font-medium text-brand-ink-strong hover:bg-brand-100"
            >
              {c.name}
            </button>
          ))}
        </div>
      );
    }
    if (bk.kind === 'cancel') {
      return (
        <div className="mt-1.5 flex flex-col items-start gap-1">
          {bk.res.appointments.map((a) => (
            <button
              key={a.appointmentId}
              disabled={busyAction === i}
              onClick={() => cancelAppt(bk.res, a, i)}
              className="rounded-lg border border-red-300 bg-red-50 px-3 py-1.5 text-left text-xs font-medium text-red-700 hover:bg-red-100 disabled:opacity-50"
            >
              {t('assistant.bkCancelBtn', 'Cancel {{when}} · {{doctor}}', { when: fmtSlot(a.startUtc), doctor: a.doctorName })}
            </button>
          ))}
        </div>
      );
    }
    return (
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {bk.res.slots.map((s) => (
          <button
            key={s.startUtc + s.seatId}
            disabled={busyAction === i}
            onClick={() => bookSlot(bk.res, s, i)}
            className="rounded-lg border border-brand-300 bg-brand-50 px-3 py-1.5 text-xs font-medium text-brand-ink-strong hover:bg-brand-100 disabled:opacity-50"
          >
            {fmtSlot(s.startUtc)} · {s.doctorName}
          </button>
        ))}
      </div>
    );
  };

  // Push-to-talk on any page: speak → Whisper transcribes it server-side → sent straight to the
  // assistant. This used the browser's Web Speech API, which mishears dental vocabulary and does
  // not exist in Firefox; the consultation dictation people already trust uses Whisper, so the
  // assistant now uses it too.
  const { supported: micSupported, recording: listening, busy: transcribing, toggle: toggleMic } = useVoiceInput({
    onResult: (transcript) => void send(transcript),
    onError: () => setError(t('assistant.micFailed', 'Could not transcribe that. Try again, or type it.')),
  });

  const suggestions = consultationId
    ? [t('assistant.sSummary', 'Summarise this patient'), t('assistant.sDraftLetter', 'Draft the recommendation letter for this visit')]
    : hasRecord
      ? [t('assistant.sSummary', 'Summarise this patient'), t('assistant.sAlerts', 'Any medical alerts I should know?')]
      : [t('assistant.s1', 'What can I do on this page?'), t('assistant.s2', 'How do I book an appointment?')];

  // Example commands — shown to teach the phrasing (type or speak). Localised; click to fill.
  // On an editable consultation these are clinical chart/notes edits; elsewhere they're scheduling.
  const commandExamples = consultationId && !consult.readOnly
    ? [
        t('assistant.exTooth', 'Mark tooth 26 as caries'),
        t('assistant.exRoot', 'Record root canal on tooth 36'),
        t('assistant.exNote', 'Add note: patient reports cold sensitivity'),
      ]
    : [
        t('assistant.exBook', 'Book Ana Popescu Friday at 3pm'),
        t('assistant.exReschedule', 'Reschedule Ion Marin to next Tuesday morning'),
        t('assistant.exCancel', "Cancel Maria Ionescu's appointment"),
      ];
  const examplesHeader = consultationId && !consult.readOnly
    ? t('assistant.exHeaderClinic', 'Try a chart command — type or speak:')
    : t('assistant.exHeader', 'Try a scheduling command — type or speak:');
  const examplesHint = consultationId && !consult.readOnly
    ? t('assistant.exHintClinic', 'Chart edits appear as proposals to review; notes are appended. Works in English or Romanian.')
    : t('assistant.exHint', 'Swap in a real patient name. Days, “tomorrow”, “next week” and clock times (3pm / 15:00) all work — in English or Romanian.');




  // The one line shown beside the closed bubble.
  //
  // Only ever a Blocking finding, only once per session, never while a patient is in the chair. A
  // helper that interrupts a consultation to mention an unpublished directory listing is a helper
  // people learn to close without reading — and this corner is already busy with a subscription
  // banner, an ad slot, a notification bell and the cookie banner.

  // One surface for AI + messages. Messaging is staff-wide and must survive the AI toggle, so the
  // widget only disappears if the clinic turned AI off AND this user can't message anyone.
  const canChat = role === 'TenantAdmin' || role === 'CabinetAdmin' || role === 'Doctor' || role === 'Receptionist';
  // Guidance moved to the dashboard, so the widget is purely conversational again:
  // anything, and a clinic with AI switched off is exactly the one most likely to need it.
  if (!aiEnabled && !canChat) return null;

  const tabs: Tab[] = [
    ...(aiEnabled ? (['chat'] as Tab[]) : []),
    ...(canChat ? (['messages'] as Tab[]) : []),
  ];
  const activeTab: Tab = tabs.includes(tab) ? tab : tabs[0];
  const tabLabel = (k: Tab) =>
    k === 'chat' ? t('assistant.tabChat', 'Assistant')
        : t('assistant.tabMessages', 'Messages');

  return (
    <div className="fixed bottom-6 right-6 z-40 flex flex-col items-end gap-3 print:hidden">
      {open && (
        <div className="flex h-[32rem] max-h-[calc(100vh-6rem)] w-96 max-w-[calc(100vw-3rem)] flex-col rounded-2xl border border-brand-200 bg-surface shadow-xl">
          <div className="flex items-center gap-2 border-b border-line-soft px-4 py-3">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-600 text-sm text-white" aria-hidden>✨</span>
            <span className="text-sm font-medium text-body">{t('assistant.title', 'Ask easyDent')}</span>
            <button onClick={() => setOpen(false)} className="ml-auto text-subtle-ink hover:text-muted-strong" aria-label={t('assistant.close', 'Close assistant')}>✕</button>
          </div>

          {/* Assistant · Messages · (Nurse, while a visit is open) — one place for AI and people. */}
          {tabs.length > 1 && (
            <div className="flex shrink-0 border-b border-line-soft text-sm">
              {tabs.map((k) => (
                <button
                  key={k}
                  onClick={() => setTab(k)}
                  className={`flex flex-1 items-center justify-center gap-1.5 px-3 py-2 font-medium ${activeTab === k ? 'border-b-2 border-brand-600 text-brand-ink-strong' : 'text-muted hover:bg-canvas'}`}
                >
                  {tabLabel(k)}
                  {k === 'messages' && unread > 0 && (
                    <span className="rounded-full bg-red-500 px-1.5 text-[10px] font-semibold text-white">{unread > 9 ? '9+' : unread}</span>
                  )}
                </button>
              ))}
            </div>
          )}

          {activeTab === 'messages' ? (
            <MessagesPanel />
          ) : (
          <>
          {/* On a visit, the assistant gains the clinical voice tools that used to be a separate
              "Nurse" tab: brief the dentist, read it back, dictate into the chart. They were never a
              different assistant — typed clinical commands already went through the same apply
              pipeline — only a different tab, which left people asking which one to use.

              Rendered only while this tab is active, so switching to Messages or closing the panel
              unmounts it and stops any listening exactly as the old tab did. */}
          {aiEnabled && consult.consultationId && (
            <div className="shrink-0 border-b border-line-soft">
              <NursePanel
                consultationId={consult.consultationId}
                disabled={consult.readOnly}
                onDictation={consult.onDictation ?? undefined}
              />
            </div>
          )}

          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-3">
            {messages.length === 0 && !loading && (
              <div className="space-y-2">
                <p className="text-sm text-muted">
                  {hasRecord
                    ? t('assistant.hintRecord', 'Ask about the open record (findings, treatments, balance) or how to use easyDent.')
                    : t('assistant.hint', 'Ask about this page or how to use easyDent. Open a patient or consultation to ask about it.')}
                </p>
                {suggestions.map((s) => (
                  <button key={s} onClick={() => send(s)} className="block w-full rounded-lg border border-line px-3 py-2 text-left text-xs text-body-soft hover:border-brand-300 hover:bg-brand-50/40">
                    {s}
                  </button>
                ))}

                {/* Teach the scheduling-command phrasing. Click fills the box; edit the name, then send or speak. */}
                <div className="mt-3 rounded-lg bg-canvas p-3">
                  <p className="mb-1.5 flex items-center gap-1 text-xs font-medium text-muted-strong">
                    <span aria-hidden>⌘</span>
                    {examplesHeader}
                  </p>
                  <ul className="space-y-1">
                    {commandExamples.map((ex) => (
                      <li key={ex}>
                        <button
                          onClick={() => fillExample(ex)}
                          className="w-full rounded-md px-2 py-1 text-left text-xs italic text-brand-ink-strong hover:bg-brand-50"
                        >
                          “{ex}”
                        </button>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-1.5 px-2 text-[11px] leading-snug text-subtle-ink">
                    {examplesHint}
                  </p>
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={m.role === 'user' ? 'flex flex-col items-end' : 'flex flex-col items-start'}>
                <div className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3 py-2 text-sm ${m.role === 'user' ? 'bg-brand-600 text-white' : 'bg-subtle text-body-strong'}`}>
                  {m.text}
                </div>
                {/* The answer came out of a guide — offer the page it came from, opened in the
                    drawer rather than a new tab, so the reader keeps the screen they asked about. */}
                {m.guide && (
                  <button
                    onClick={() => openHelp(m.guide!)}
                    className="mt-2 block text-xs font-medium text-brand-ink-strong hover:underline"
                  >
                    {t('help.readFullGuide', 'Read the full guide')} →
                  </button>
                )}
                {m.action && (
                  <button
                    onClick={() => runAction(i, m.action!)}
                    disabled={busyAction === i}
                    className="mt-1.5 flex items-center gap-1.5 rounded-lg border border-brand-300 bg-brand-50 px-3 py-1.5 text-xs font-medium text-brand-ink-strong hover:bg-brand-100 disabled:opacity-50"
                  >
                    <span aria-hidden>▶</span>
                    {busyAction === i ? t('assistant.working', 'Working…') : m.action.label}
                  </button>
                )}
                {m.booking && renderBooking(m.booking, i)}
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="rounded-2xl bg-subtle px-3 py-2 text-sm text-subtle-ink">{t('assistant.thinking', 'Thinking…')}</div>
              </div>
            )}
            {error && <p className="text-sm text-red-600">{error}</p>}
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); void send(input); }}
            className="flex items-center gap-2 border-t border-line-soft p-3"
          >
            {micSupported && (
              <button
                type="button"
                onClick={toggleMic}
                disabled={transcribing}
                aria-label={transcribing ? t('assistant.transcribing', 'Transcribing…') : t('assistant.speak', 'Speak')}
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border disabled:opacity-60 ${listening ? 'border-red-300 bg-red-50 text-red-600' : 'border-line-strong text-muted hover:bg-canvas'}`}
              >
                {transcribing ? (
                  <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-line-strong border-t-brand-600" />
                ) : listening ? (
                  <span className="inline-block h-3 w-3 animate-pulse rounded-full bg-red-500" />
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2M12 19v4" />
                  </svg>
                )}
              </button>
            )}
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={transcribing ? t('assistant.transcribing', 'Transcribing…') : listening ? t('assistant.listening', 'Listening…') : t('assistant.placeholder', 'Ask a question…')}
              className="flex-1 rounded-lg border border-line-strong px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            <button type="submit" disabled={!input.trim() || loading} className="rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50">
              {t('assistant.send', 'Send')}
            </button>
          </form>
          </>
          )}
        </div>
      )}

      {/*
        * One line, once, and only for something that stops the clinic working. Everything else waits
        * to be asked for: this corner is already crowded (subscription banner, ad slot, bell, cookie banner).
        */}

      <button
        onClick={() => setOpen(!open)}
        aria-label={open ? t('assistant.close', 'Close assistant') : t('assistant.title', 'Ask easyDent')}
        className="relative flex h-14 w-14 items-center justify-center rounded-full bg-brand-600 text-white shadow-lg transition hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2"
      >
        {open ? (
          <span className="text-xl">✕</span>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
          </svg>
        )}
        {/* One meaning: somebody is waiting for a reply. It used to double as the setup-findings
            count in amber, and the two shared one slot with unread winning the tie — so a clinic
            with three blocking findings and one unread message saw "1". Setup lives on the
            dashboard now, and this badge says exactly one thing. */}
        {!open && unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[11px] font-semibold text-white ring-2 ring-white">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
    </div>
  );
};

export default AiAssistant;
